import { FC, memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { BorderedCard } from 'components/BorderedCard';
import { ISectionRowProps as IProps } from 'components/BusinessToolForm';
import { TooltipFormImageUpload } from 'components/BusinessToolForm/components/TooltipFormImageUpload';

import { concatWithDot } from 'helpers/string';

import { WorkExperience } from './WorkExperience';
import { LIST_OF_EXTERNAL_PARTNERS_NAMES, workExperienceInitialValues } from '../utils/form';

import styles from './ExternalPartnersProfiles.module.scss';

export const ExternalPartnersProfiles: FC<IProps> = memo(({ name, disabled = false }) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  return (
    <BorderedCard>
      <Grid container spacing={3}>
        <Grid item xs="auto">
          <TooltipFormImageUpload
            width={136}
            height={136}
            name={concatWithDot(name, LIST_OF_EXTERNAL_PARTNERS_NAMES.FILE)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextInputControl
                control={control}
                id={concatWithDot(name, LIST_OF_EXTERNAL_PARTNERS_NAMES.NAME)}
                name={concatWithDot(name, LIST_OF_EXTERNAL_PARTNERS_NAMES.NAME)}
                label={t('management.partners.name')}
                disabled={disabled}
                maxLength={150}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInputControl
                control={control}
                id={concatWithDot(name, LIST_OF_EXTERNAL_PARTNERS_NAMES.SURNAME)}
                name={concatWithDot(name, LIST_OF_EXTERNAL_PARTNERS_NAMES.SURNAME)}
                label={t('management.partners.surname')}
                disabled={disabled}
                maxLength={150}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInputControl
                control={control}
                id={concatWithDot(name, LIST_OF_EXTERNAL_PARTNERS_NAMES.ROLE)}
                name={concatWithDot(name, LIST_OF_EXTERNAL_PARTNERS_NAMES.ROLE)}
                label={t('management.partners.role')}
                disabled={disabled}
                maxLength={150}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <FormSectionFilledCard title={t('management.partners.workExperience')}>
            <FieldArrayComponent
              fieldArrayStyles={{ removeButton: styles.removeButton, addButton: styles.addButton }}
              name={concatWithDot(name, LIST_OF_EXTERNAL_PARTNERS_NAMES.WORK_EXPERIENCE)}
              initialItem={workExperienceInitialValues}
              buttonText={t('management.partners.workExperienceButtonText')}
              disabled={disabled}
            >
              {(namePrefix) => <WorkExperience name={namePrefix} disabled={disabled} />}
            </FieldArrayComponent>
          </FormSectionFilledCard>
        </Grid>
        <Grid item xs={12}>
          <TextInputControl
            multiline
            control={control}
            id={concatWithDot(name, LIST_OF_EXTERNAL_PARTNERS_NAMES.KEY_SKILLS)}
            name={concatWithDot(name, LIST_OF_EXTERNAL_PARTNERS_NAMES.KEY_SKILLS)}
            minRows={4}
            label={t('management.team.keySkills')}
            disabled={disabled}
            maxLength={500}
          />
        </Grid>
      </Grid>
    </BorderedCard>
  );
});
